import type { Factura, ItemFactura, Cliente, CreditoCrearConfig } from '../../components/facturas/types';
import type { FacturaFormState, FacturaTotales } from '../../shared/nuevaFactura/facturaFormTypes';
import { lineEconomicBreakdown } from '../../shared/nuevaFactura/lineEconomics';
import { METODO_PAGO_SISTEMA_CREDITO_ID } from './sistemaMetodoCredito';
import {
  descuentosParaPersistir,
  notasConAjusteCredito,
  textoAjusteCredito,
} from '../../shared/nuevaFactura/ajusteCreditoTotal';

const LEGACY_METODO_DB: Record<string, string> = {
  efectivo: 'EFECTIVO',
  transferencia: 'TRANSFERENCIA',
  tarjeta_debito: 'TARJETA_DEBITO',
  nequi: 'NEQUI',
  daviplata: 'DAVIPLATA',
  pse: 'PSE',
};

const CREDITO_TYPE_KEYS = new Set([
  'credito',
  'credit',
  'venta_credito',
  'venta_a_credito',
  'fiado',
  'pago_diferido',
]);

/**
 * Método de pago a crédito: por `method_type` o por nombre visible (p. ej. "Crédito" con type `otro` en config).
 * Evita confundir con "Tarjeta de crédito" (no se usa el nombre suelto para eso).
 */
export function isCreditoPaymentMethod(pm: {
  method_type?: string | null;
  method_name?: string | null;
}): boolean {
  const t = String(pm?.method_type ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  if (CREDITO_TYPE_KEYS.has(t)) return true;
  const n = String(pm?.method_name ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
  if (n === 'credito' || n === 'credit') return true;
  if (n === 'venta a credito' || n.startsWith('venta a credito ')) return true;
  if (n === 'fiado') return true;
  if (n === 'pago diferido' || n.startsWith('pago diferido ')) return true;
  return false;
}

/** Valor de `facturas.metodo_pago` coherente con `isCreditoPaymentMethod` (CREDITO vs tipo mapeado). */
export function metodoPagoDbDesdeMetodoPago(pm: {
  method_type?: string | null;
  method_name?: string | null;
}): string {
  if (isCreditoPaymentMethod(pm)) return 'CREDITO';
  return paymentMethodTypeToFacturaDb(pm.method_type);
}

/** Mapea `payment_methods.method_type` (o claves legacy) al texto guardado en `facturas.metodo_pago`. */
export function paymentMethodTypeToFacturaDb(method_type: string | undefined | null): string {
  const s = String(method_type ?? '').trim();
  const k = s.toLowerCase().replace(/-/g, '_');
  const map: Record<string, string> = {
    efectivo: 'EFECTIVO',
    transferencia: 'TRANSFERENCIA',
    nequi: 'NEQUI',
    daviplata: 'DAVIPLATA',
    pse: 'PSE',
    tarjeta: 'TARJETA',
    tarjeta_debito: 'TARJETA_DEBITO',
    link_pago: 'LINK_PAGO',
    qr_code: 'QR_CODE',
    otro: 'OTRO',
    credito: 'CREDITO',
    credit: 'CREDITO',
    venta_credito: 'CREDITO',
    venta_a_credito: 'CREDITO',
    fiado: 'CREDITO',
    pago_diferido: 'CREDITO',
  };
  return map[k] ?? s.toUpperCase().replace(/-/g, '_');
}

function isLikelyUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s.trim());
}

export type BuildFacturaNuevaOpciones = {
  /** Valor normalizado para `facturas.metodo_pago` (p. ej. `EFECTIVO`). Obligatorio si `form.metodo_pago` es UUID. */
  metodoPagoDb?: string;
  /** Monto cobrado ahora (parcial o total). Si se omite, se usa el toggle «Marcar como pagado». */
  montoPagado?: number;
  /** Crédito: el abono de hoy sigue viniendo en `montoPagado` (no se fuerza a 0). */
  credito?: boolean;
  /** Solo si `credito` y el usuario definió fechas de cobro en el asistente. */
  creditoCrearConfig?: CreditoCrearConfig;
  mensajeRegalo?: string;
  direccionRegalo?: string;
  fechaEntregaAgendada?: Date | null;
};

function resolveMetodoPagoDb(form: FacturaFormState, opts?: BuildFacturaNuevaOpciones): string {
  if (opts?.metodoPagoDb) return opts.metodoPagoDb;
  const raw = (form.metodo_pago || '').trim();
  if (raw === METODO_PAGO_SISTEMA_CREDITO_ID) return 'CREDITO';
  const legacy = LEGACY_METODO_DB[raw];
  if (legacy) return legacy;
  if (isLikelyUuid(raw)) return 'EFECTIVO';
  const mapped = paymentMethodTypeToFacturaDb(raw);
  return mapped || 'EFECTIVO';
}

function mapClienteFormACFactura(c: FacturaFormState['cliente']): Cliente | null {
  if (!c) return null;
  const parts = c.nombre.trim().split(/\s+/);
  return {
    id: c.id,
    nombre: parts[0] || c.nombre,
    apellido: parts.length > 1 ? parts.slice(1).join(' ') : undefined,
    telefono: c.telefono,
    email: c.email,
    numeroDocumento: c.cedula,
  };
}

function productosAItems(productos: FacturaFormState['productos']): ItemFactura[] {
  return productos.map((p, idx) => {
    const qty = p.qty;
    const u = lineEconomicBreakdown(p);
    const precioUnitario = p.precio;
    const precioSinIvaUnit = qty > 0 ? u.subtotalSinIva / qty : 0;
    const valorIvaUnit = qty > 0 ? u.totalIva / qty : 0;
    return {
      id: String(p.id || `line-${idx}`),
      productoId: p.id,
      producto: p.nombre,
      descripcion: '',
      cantidad: qty,
      precioUnitario,
      subtotal: u.subtotalConIva,
      ivaPorcentaje: u.ivaPorcentaje,
      precioSinIva: precioSinIvaUnit,
      valorIva: valorIvaUnit,
      subtotalSinIva: u.subtotalSinIva,
      totalIva: u.totalIva,
      requiereInstalacion: false,
    };
  });
}

function mapTipoDescuento(t: FacturaFormState['descuento_tipo']): Factura['tipoDescuento'] {
  if (t === 'porcentaje') return 'PORCENTAJE';
  if (t === 'valor') return 'MONTO_FIJO';
  return 'NINGUNO';
}

/**
 * Construye un borrador `Factura` para `useFacturasStore.createFactura` desde el wizard compartido.
 * Los importes en Supabase siguen `nuevaFlujoTotales` (descuento proporcional sobre el bruto con IVA por línea).
 */
export function buildFacturaNuevaDesdeFormulario(
  form: FacturaFormState,
  totales: FacturaTotales,
  opts?: BuildFacturaNuevaOpciones
): Factura {
  const items = productosAItems(form.productos);
  const subtotalSinIva = items.reduce((s, i) => s + i.subtotalSinIva, 0);
  const totalIvaItems = items.reduce((s, i) => s + i.totalIva, 0);
  const subtotalConIva = items.reduce((s, i) => s + i.subtotal, 0);
  const total = totales.total;
  const pagadoToggle = form.estado_pago === 'pagado';
  let paid: number;
  if (opts?.montoPagado !== undefined) {
    paid = Math.max(0, Math.min(Number(opts.montoPagado) || 0, total));
  } else {
    paid = pagadoToggle ? total : 0;
  }
  const saldo = Math.max(0, total - paid);

  let estadoPago: Factura['estadoPago'];
  if (opts?.credito) {
    if (paid >= total) estadoPago = 'PAGADO';
    else if (paid > 0) estadoPago = 'PAGO_PARCIAL';
    else estadoPago = 'PENDIENTE_PAGO';
  } else if (paid >= total) estadoPago = 'PAGADO';
  else if (paid > 0) estadoPago = 'PAGO_PARCIAL';
  else estadoPago = 'PENDIENTE_PAGO';

  const fechaEntrega = new Date(`${form.fecha_entrega}T12:00:00`);

  const cliente = mapClienteFormACFactura(form.cliente);
  const mensajeR = (opts?.mensajeRegalo ?? '').trim();
  const direccionR = (opts?.direccionRegalo ?? '').trim();
  const fechaAg = opts?.fechaEntregaAgendada ?? undefined;
  const hayCitaEntrega = Boolean(fechaAg);

  const notasEntregaLimpias = (form.notas_entrega || '').trim();
  const notasEntregaTagged = form.cobrar_envio_al_destino
    ? `[cobrar_al_destino]${notasEntregaLimpias ? ` ${notasEntregaLimpias}` : ''}`.trim()
    : notasEntregaLimpias;

  const textoAjuste = textoAjusteCredito({
    tipo: form.credito_ajuste_tipo,
    valor: form.credito_ajuste_valor,
    sentido: form.credito_ajuste_sentido,
    ajusteCop: totales.ajuste_credito || 0,
  });

  const result: Factura = {
    id: '',
    numero: form.numero_factura.trim() || `WEB-${Date.now().toString().slice(-8)}`,
    cliente,
    fechaCreacion: new Date(),
    fechaEntrega,
    estadoPago,
    estadoEntrega: 'PENDIENTE_ENTREGA',
    estado: 'ENVIADA',
    items,
    subtotalSinIva,
    totalIva: totalIvaItems,
    subtotal: subtotalConIva,
    descuento: descuentosParaPersistir(totales),
    total,
    notas: notasConAjusteCredito(form.notas, textoAjuste),
    condiciones: '',
    metodoPago: resolveMetodoPagoDb(form, opts),
    mensajesEnviados: [],
    ultimaActividad: new Date(),
    montoPagado: paid,
    montoPendiente: saldo,
    fechaPago: paid >= total ? new Date() : undefined,
    tipoEntrega: form.tipo_entrega === 'domicilio' ? 'DOMICILIO' : 'TIENDA',
    metodoEntrega: form.metodo_entrega,
    costoEnvio: Math.max(0, Math.round(Number(form.costo_envio) || 0)),
    notasEntrega: notasEntregaTagged || undefined,
    direccionEntrega: form.direccion_entrega || '',
    ciudadEntrega: '',
    barrioEntrega: '',
    instruccionesEntrega: '',
    contactoEntrega: '',
    telefonoEntrega: '',
    tipoDescuento: mapTipoDescuento(form.descuento_tipo),
    valorDescuento: form.descuento_valor,
    descripcionDescuento: '',
    fechaAgendada: fechaEntrega,
    horaAgendada: '10:00',
    vendedor: form.vendedor_id || '',
    canalVenta: 'TIENDA',
    prioridad: 'MEDIA',
    requiereInstalacion: false,
    garantia: '',
    observacionesInternas: '',
    esRegalo: form.es_regalo,
    mensajeRegalo: form.es_regalo ? mensajeR : '',
    direccionRegalo: form.es_regalo ? direccionR : '',
    motivoRegalo: '',
    destinatarioRegalo: '',
    ocasionRegalo: 'OTRO',
    incluirTarjetaRegalo: false,
    mensajeTarjeta: '',
    nuevaFlujoTotales: totales,
    agendarCalendario: hayCitaEntrega,
    fechaEntregaAgendada: fechaAg,
    ...(opts?.credito && opts.creditoCrearConfig
      ? { creditoCrearConfig: opts.creditoCrearConfig }
      : {}),
  };
  console.log('=== buildFactura creditoCrearConfig ===', result.creditoCrearConfig);
  return result;
}
